export const RESOURCES = ['wood', 'brick', 'sheep', 'wheat', 'ore'] as const;
export type Resource = (typeof RESOURCES)[number];
export type Terrain = Resource | 'desert';
export type ResourceCounts = Record<Resource, number>;

export type DevCardType = 'knight' | 'roadBuilding' | 'yearOfPlenty' | 'monopoly' | 'victoryPoint';

export const PLAYER_COUNT = 4;
export const WINNING_POINTS = 10;

/** A hex tile. `q`/`r` are axial coordinates; `x`/`y` are layout units for drawing. */
export interface Hex {
  id: number;
  q: number;
  r: number;
  x: number;
  y: number;
  terrain: Terrain;
  /** Dice number, or null for the desert. */
  token: number | null;
  vertices: number[];
}

export interface Vertex {
  id: number;
  x: number;
  y: number;
  hexes: number[];
  edges: number[];
  neighbors: number[];
  /** Harbor index into `Board.harbors`, if this vertex touches one. */
  harbor: number | null;
}

export interface Edge {
  id: number;
  vertices: [number, number];
  hexes: number[];
}

export interface Harbor {
  /** `any` means a 3:1 harbor; a resource means 2:1 for that resource. */
  kind: Resource | 'any';
  edge: number;
}

export interface Board {
  hexes: Hex[];
  vertices: Vertex[];
  edges: Edge[];
  harbors: Harbor[];
}

export interface OwnedDevCard {
  type: DevCardType;
  boughtOnTurn: number;
}

export interface Player {
  name: string;
  color: string;
  resources: ResourceCounts;
  devCards: OwnedDevCard[];
  knightsPlayed: number;
  roadsLeft: number;
  settlementsLeft: number;
  citiesLeft: number;
}

export interface Building {
  player: number;
  type: 'settlement' | 'city';
}

export interface TradeOffer {
  from: number;
  give: ResourceCounts;
  get: ResourceCounts;
  /** Player index -> true (accepted) / false (declined). Missing = no answer yet. */
  responses: Record<number, boolean>;
}

export type Phase = 'setup' | 'main' | 'ended';

/**
 * What the current player must do next in the main phase.
 * - roll: must roll (or play a knight first)
 * - discard: waiting on players in `pendingDiscards` after a 7
 * - moveRobber: current player must move the robber
 * - actions: free to build, trade, play cards, end turn
 */
export type TurnStep = 'roll' | 'discard' | 'moveRobber' | 'actions';

export interface SetupState {
  /** Seat order for the snake draft, e.g. [2,3,0,1,1,0,3,2]. */
  order: number[];
  index: number;
  step: 'settlement' | 'road';
  lastSettlement: number | null;
}

export interface GameState {
  board: Board;
  players: Player[];
  buildings: Record<number, Building>;
  roads: Record<number, number>;
  robberHex: number;
  bank: ResourceCounts;
  devDeck: DevCardType[];
  phase: Phase;
  setup: SetupState;
  currentPlayer: number;
  turn: number;
  turnStep: TurnStep;
  /** After the robber moves, which step to return to (actions, or roll if a knight was played first). */
  afterRobber: TurnStep;
  pendingDiscards: Record<number, number>;
  dice: [number, number] | null;
  devCardPlayedThisTurn: boolean;
  freeRoads: number;
  trade: TradeOffer | null;
  longestRoad: { player: number; length: number } | null;
  largestArmy: { player: number; size: number } | null;
  winner: number | null;
  log: string[];
  rngState: number;
}

export type Action =
  | { type: 'placeSettlement'; player: number; vertex: number }
  | { type: 'placeRoad'; player: number; edge: number }
  | { type: 'rollDice'; player: number }
  | { type: 'discard'; player: number; resources: Partial<ResourceCounts> }
  | { type: 'moveRobber'; player: number; hex: number; victim: number | null }
  | { type: 'buildRoad'; player: number; edge: number }
  | { type: 'buildSettlement'; player: number; vertex: number }
  | { type: 'buildCity'; player: number; vertex: number }
  | { type: 'buyDevCard'; player: number }
  | { type: 'playKnight'; player: number }
  | { type: 'playRoadBuilding'; player: number }
  | { type: 'playYearOfPlenty'; player: number; resources: [Resource, Resource] }
  | { type: 'playMonopoly'; player: number; resource: Resource }
  | { type: 'bankTrade'; player: number; give: Resource; get: Resource }
  | { type: 'offerTrade'; player: number; give: Partial<ResourceCounts>; get: Partial<ResourceCounts> }
  | { type: 'respondTrade'; player: number; accept: boolean }
  | { type: 'confirmTrade'; player: number; with: number }
  | { type: 'cancelTrade'; player: number }
  | { type: 'endTurn'; player: number };

export class GameError extends Error {}
